import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { api } from "../lib/api";
import { Spark } from "../components/Spark";
import { SITE_NAME } from "../constants/site";
import {
  ArrowRight, GraduationCap, Layers, ListChecks,
  Brain, Radar, Check, X, Crown, Sparkles, ChevronDown, ShieldCheck,
  UserRound, Calendar, Lock, EyeOff, Trash2, KeyRound, Camera, Flame,
  Star, Quote,
} from "lucide-react";

const ORDER = ["gratis", "medio", "pro"];

// Fallback caso a API demore/caia — a landing nunca fica sem os planos.
const FALLBACK_PLANS = {
  precos: { gratis: 0, medio: 19.9, pro: 39.9 },
  nomes: { gratis: "Grátis", medio: "Médio", pro: "Pro" },
  tabela: [
    { recurso: "Chat tira-dúvidas", gratis: "15 msg/dia", medio: "Ilimitado", pro: "Ilimitado" },
    { recurso: "Modo Prova (foco no conteúdo)", gratis: false, medio: true, pro: true },
    { recurso: "Flashcards + revisão espaçada", gratis: "Básico", medio: true, pro: true },
    { recurso: "Quiz / Simulado", gratis: "3/dia", medio: "Ilimitado", pro: "Ilimitado + cronometrado" },
    { recurso: "Upload de material (PDF/foto)", gratis: false, medio: true, pro: true },
    { recurso: "Mapa mental interativo", gratis: false, medio: false, pro: true },
  ],
};

// Ritmo visual: faixas alternam entre a base e a superfície elevada (nunca 2
// vizinhas iguais), com divisória em onda na cor da PRÓXIMA faixa.
const TONES = { base: "#0F0F1A", raised: "#1A1A2E" };

function Wave({ fill, flip = false }) {
  return (
    <div aria-hidden="true" className="-mb-px leading-[0]">
      <svg
        viewBox="0 0 1440 72"
        preserveAspectRatio="none"
        className="block h-10 w-full sm:h-14 lg:h-[72px]"
        style={flip ? { transform: "scaleX(-1)" } : undefined}
      >
        <path d="M0,44 C240,76 480,4 720,26 C960,48 1200,66 1440,20 L1440,72 L0,72 Z" fill={fill} />
      </svg>
    </div>
  );
}

// Scroll-reveal (fade + slide-up) com framer-motion. useReducedMotion desliga
// tudo pra quem pediu menos movimento no sistema.
function Reveal({ children, className = "", delay = 0 }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: delay / 1000 }}
    >
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState(null);
  const [depoimentos, setDepoimentos] = useState([]);

  useEffect(() => {
    api.get("/plans", { silent: true }).then((r) => setPlans(r.data)).catch(() => setPlans(FALLBACK_PLANS));
    api.get("/depoimentos", { silent: true }).then((r) => setDepoimentos(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const p = plans || FALLBACK_PLANS;

  // Cada entrada vira uma faixa; o tom alterna pela posição, então mesmo com a
  // faixa condicional de depoimentos as vizinhas nunca ficam iguais.
  const bands = [
    { id: "hero", depth: true, render: () => <Hero /> },
    { id: "diferencial", render: (surface) => <Diferencial surface={surface} /> },
    { id: "showcase", render: (surface) => <StickyShowcase surface={surface} /> },
    { id: "como-funciona", render: (surface) => <ComoFunciona surface={surface} /> },
    { id: "features", render: (surface) => <FeaturesGrid surface={surface} /> },
    { id: "privacidade", render: (surface) => <Privacidade surface={surface} /> },
    ...(depoimentos.length > 0
      ? [{ id: "depoimentos", render: (surface) => <Depoimentos surface={surface} items={depoimentos} /> }]
      : []),
    { id: "planos", render: (surface) => <Planos surface={surface} p={p} /> },
    { id: "faq", render: (surface) => <FaqSection surface={surface} /> },
    { id: "cta-final", render: () => <CtaFinal /> },
  ];

  // overflow-x-clip (e não hidden): hidden viraria scroll-container e mataria o sticky do showcase
  return (
    <div data-testid="landing-page" className="dark min-h-screen bg-background text-foreground relative overflow-x-clip">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5 min-w-0">
            <Spark className="h-8 w-8 shrink-0" />
            <span className="font-bold tracking-tight whitespace-nowrap text-sm sm:text-base">
              {SITE_NAME}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link
              to="/login"
              data-testid="landing-login-link"
              className="rounded-full px-3 sm:px-4 py-2.5 min-h-[44px] inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 whitespace-nowrap"
            >
              Entrar
            </Link>
            <Link
              to="/planos"
              data-testid="landing-signup-nav"
              className="rounded-full bg-violet-600 hover:bg-violet-500 text-white px-3.5 sm:px-4 py-2.5 min-h-[44px] inline-flex items-center text-sm font-medium transition duration-200 glow-violet whitespace-nowrap"
            >
              Quero entrar
            </Link>
          </div>
        </div>
      </header>

      <main>
        {bands.map((band, i) => {
          const tone = i % 2 === 0 ? "base" : "raised";
          // Cards em faixa elevada sobem um degrau de superfície pra não sumir no fundo.
          const surface = tone === "raised" ? "bg-popover" : "bg-card";
          const next = i + 1 < bands.length ? (i + 1) % 2 === 0 ? TONES.base : TONES.raised : null;
          return (
            <section
              key={band.id}
              id={band.id}
              className={`scroll-mt-20 ${band.depth ? "bg-depth" : ""}`}
              style={band.depth ? undefined : { backgroundColor: TONES[tone] }}
            >
              <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
                {band.render(surface)}
              </div>
              {next && <Wave fill={next} flip={i % 2 === 1} />}
            </section>
          );
        })}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/60" style={{ backgroundColor: TONES.base }}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Spark className="h-5 w-5" />
            <span>{SITE_NAME} · {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/termos" className="hover:text-foreground transition-colors duration-200">Termos de Uso</Link>
            <Link to="/privacidade" className="hover:text-foreground transition-colors duration-200">Privacidade</Link>
            <a href="https://wa.me/5531990682809" target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors duration-200">Contato</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ================= Seções ================= */

function Hero() {
  return (
    <>
    <div className="grid gap-12 lg:grid-cols-2 lg:items-center py-2 sm:py-6">
      <div className="space-y-6 text-center lg:text-left">
        <span className="anim-rise inline-flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-300">
          <Sparkles className="h-3.5 w-3.5" /> Não é mais um chat genérico
        </span>
        <h1 className="anim-rise text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.08]" style={{ animationDelay: "80ms" }}>
          O tutor de IA que{" "}
          <span className="text-gradient-violet">te conhece</span>
        </h1>
        <p className="anim-rise text-muted-foreground sm:text-lg max-w-md mx-auto lg:mx-0" style={{ animationDelay: "160ms" }}>
          Ele aprende como você estuda, foca no que te trava e adapta cada explicação ao seu jeito. Quanto mais você usa, melhor ele fica.
        </p>
        <div className="anim-rise flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start" style={{ animationDelay: "240ms" }}>
          <Link
            to="/planos"
            data-testid="landing-hero-cta"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-medium px-7 py-3.5 transition duration-200 glow-violet"
          >
            Quero entrar <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="#diferencial"
            className="w-full sm:w-auto inline-flex items-center justify-center rounded-full border border-border hover:border-violet-500/50 px-7 py-3.5 text-sm font-medium text-muted-foreground hover:text-foreground transition duration-200"
          >
            Por que é diferente?
          </a>
        </div>
        <p className="anim-rise text-xs text-muted-foreground" style={{ animationDelay: "320ms" }}>
          Sem cartão. O plano Grátis é de verdade.
        </p>
      </div>

      {/* Mockup animado: o tutor citando o perfil do aluno */}
      <div className="relative mx-auto w-full max-w-md anim-float lg:rotate-2">
        <div className="rounded-2xl border border-border bg-card p-4 shadow-2xl shadow-black/40">
          <div className="flex items-center justify-between border-b border-border/60 pb-3 mb-3">
            <div className="flex items-center gap-2">
              <Spark className="h-6 w-6" />
              <span className="text-sm font-semibold">Seu tutor</span>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 uppercase tracking-wide">
              <UserRound className="h-3 w-3" /> Perfil ativo
            </span>
          </div>
          <div className="space-y-3 text-sm">
            <div className="anim-msg rounded-lg border border-border bg-secondary px-3 py-2 text-[11px] text-muted-foreground" style={{ animationDelay: "400ms" }}>
              <span className="font-semibold text-violet-300">Perfil da Lara:</span> aprende com exemplos · trava em interpretação · meta: recuperar Química
            </div>
            <div className="anim-msg flex justify-end" style={{ animationDelay: "800ms" }}>
              <div className="max-w-[85%] rounded-2xl rounded-br-md bg-violet-600 text-white px-4 py-2.5">
                Me ajuda a revisar pra prova de quinta? 😩
              </div>
            </div>
            <div className="anim-msg flex justify-start" style={{ animationDelay: "1400ms" }}>
              <div className="max-w-[90%] rounded-2xl rounded-bl-md bg-secondary px-4 py-2.5 space-y-1.5">
                <p>Bora! Vi que essa semana você errou mais em <strong>Química orgânica</strong> — 3 vezes em nomenclatura de cadeias.</p>
                <p className="text-muted-foreground">Como você aprende melhor com exemplos, vou te mostrar 2 casos reais antes da teoria. 🎯</p>
              </div>
            </div>
            <div className="anim-msg flex justify-start" style={{ animationDelay: "2000ms" }}>
              <div className="rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs text-violet-300">
                ⚡ Quiz no estilo da SUA prova: nomenclatura de cadeias
              </div>
            </div>
            <div className="anim-msg flex items-center gap-1 px-2 pt-1" style={{ animationDelay: "2600ms" }}>
              <span className="h-1.5 w-1.5 rounded-full bg-violet-400 typing-dot" />
              <span className="h-1.5 w-1.5 rounded-full bg-violet-400 typing-dot" style={{ animationDelay: "0.2s" }} />
              <span className="h-1.5 w-1.5 rounded-full bg-violet-400 typing-dot" style={{ animationDelay: "0.4s" }} />
            </div>
          </div>
        </div>
        {/* badge flutuante do radar */}
        <div className="anim-msg absolute -bottom-5 -left-3 sm:-left-8 rounded-xl border border-border bg-popover px-3.5 py-2.5 shadow-xl shadow-black/30 flex items-center gap-2.5" style={{ animationDelay: "2200ms" }}>
          <div className="rounded-lg bg-violet-500/15 p-1.5">
            <Radar className="h-4 w-4 text-violet-400" />
          </div>
          <div className="leading-tight">
            <p className="text-[11px] font-semibold">Radar de pontos fracos</p>
            <p className="text-[10px] text-muted-foreground">atualizado a cada quiz</p>
          </div>
        </div>
      </div>
    </div>

    {/* cue de scroll: convite pra descer e ver os diferenciais */}
    <a
      href="#showcase"
      data-testid="hero-scroll-cue"
      className="scroll-cue mt-14 sm:mt-16 flex flex-col items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      aria-label="Role pra ver mais"
    >
      <span>role pra ver mais</span>
      <ChevronDown className="scroll-cue-arrow h-5 w-5 text-violet-400" />
    </a>
    </>
  );
}

// Comparação "ChatGPT genérico × seu tutor que te conhece"
function Diferencial({ surface }) {
  return (
    <div className="space-y-10">
      <Reveal className="text-center space-y-3 max-w-2xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Personalizado muda tudo</h2>
        <p className="text-muted-foreground">
          Um chat genérico responde qualquer pergunta de qualquer pessoa — e esquece você no segundo seguinte.
          Um tutor que te conhece ensina <em className="text-foreground not-italic font-semibold">você</em>.
        </p>
      </Reveal>
      <div className="grid gap-4 md:grid-cols-2 max-w-4xl mx-auto">
        <Reveal>
          <div className={`h-full rounded-2xl border border-border ${surface} p-6 space-y-4`}>
            <p className="font-bold tracking-tight text-muted-foreground">Chat de IA genérico</p>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <CompareRow bad texto="Responde igual pra todo mundo" />
              <CompareRow bad texto="Não sabe o que cai na SUA prova" />
              <CompareRow bad texto="Esquece seus erros de ontem" />
              <CompareRow bad texto="Explica do jeito dele, não do seu" />
              <CompareRow bad texto="Você recomeça do zero toda conversa" />
            </ul>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className={`h-full rounded-2xl border border-violet-500/50 ${surface} p-6 space-y-4 ring-violet`}>
            <div className="font-bold tracking-tight flex items-center gap-2">
              <Spark className="h-5 w-5" /> Seu tutor que te conhece
            </div>
            <ul className="space-y-3 text-sm">
              <CompareRow texto="Adapta cada explicação ao SEU jeito de aprender" />
              <CompareRow texto="Trava o foco no conteúdo exato da sua prova" />
              <CompareRow texto="Lembra onde você errou e volta lá com você" />
              <CompareRow texto="Usa o tom que você prefere: direto, detalhado, leve" />
              <CompareRow texto="Fica melhor a cada uso — é SEU tutor" />
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

/* ---- Showcase sticky-scroll: os 4 diferenciais, uma ideia por vez ---- */

const SCENES = [
  {
    id: "tutor",
    badge: "Tutor pessoal",
    titulo: "Ele te conhece — e trava na SUA prova",
    texto: "O tutor lembra do seu perfil, dos seus erros e do que cai na sua prova. Com o Modo Prova ativo, ele para de abranger: chat, quiz e flashcards giram só em torno do conteúdo exato.",
    render: (surface) => <MockChat surface={surface} />,
  },
  {
    id: "foto",
    badge: "No celular",
    titulo: "Estudou numa folha? Tira foto e manda no chat",
    texto: "Fotografa a atividade do caderno e envia na conversa. A IA lê a imagem, corrige o que você errou e explica o porquê — como um professor olhando o seu caderno.",
    render: (surface) => <MockPhone surface={surface} />,
  },
  {
    id: "rotina",
    badge: "Rotina",
    titulo: "Sua semana num calendário de verdade",
    texto: "Escola, treino e blocos de estudo num calendário que você arrasta e solta. O tutor enxerga sua rotina e sugere estudar nos horários que você realmente tem livres.",
    render: (surface) => <MockRotina surface={surface} />,
  },
  {
    id: "hub",
    badge: "Hub e progresso",
    titulo: "Abre e já sabe o que fazer",
    texto: "A próxima prova com contagem regressiva, sua ofensiva de estudo e onde focar agora — tudo na primeira tela, alimentado pelo que você acerta e erra nos quizzes.",
    render: (surface) => <MockHub surface={surface} />,
  },
];

// Desktop: mockup fica sticky de um lado e os textos rolam do outro; a cena que
// cruza o meio da viewport vira a ativa e troca o mockup. Mobile: blocos
// empilhados de altura normal, sem sticky nem tela gigante.
function StickyShowcase({ surface }) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const sceneRefs = useRef([]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number(e.target.dataset.scene));
        });
      },
      // faixa fina no meio da viewport: ativa a cena que estiver cruzando o centro
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    sceneRefs.current.forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scene = SCENES[active];

  return (
    <div>
      <Reveal className="text-center space-y-2 max-w-2xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Na prática, é assim</h2>
        <p className="text-sm text-muted-foreground">Role e veja cada diferencial em ação — uma ideia de cada vez.</p>
      </Reveal>

      {/* Desktop: sticky + cenas rolando */}
      <div className="hidden lg:grid lg:grid-cols-2 lg:gap-16 mt-6">
        <div className="relative">
          <div className="sticky top-24 flex h-[calc(100vh-8rem)] items-center">
            <div className="w-full max-w-md mx-auto">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={scene.id}
                  initial={reduce ? false : { opacity: 0, y: 28, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={reduce ? undefined : { opacity: 0, y: -28, scale: 0.96 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="shadow-[0_28px_70px_-28px_rgba(0,0,0,0.7)] rounded-2xl">
                    {scene.render(surface)}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
        <div>
          {SCENES.map((s, i) => (
            <div
              key={s.id}
              ref={(el) => { sceneRefs.current[i] = el; }}
              data-scene={i}
              data-testid={`showcase-scene-${s.id}`}
              className="flex min-h-[85vh] items-center"
            >
              <div
                className={`space-y-4 transition-all duration-500 ${
                  active === i ? "opacity-100 translate-y-0" : "opacity-30 translate-y-1"
                }`}
              >
                <span className="inline-flex items-center rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-300">
                  {s.badge}
                </span>
                <h3 className="text-3xl xl:text-4xl font-bold tracking-tight">{s.titulo}</h3>
                <p className="text-muted-foreground max-w-md">{s.texto}</p>
                <div className="flex gap-1.5 pt-2">
                  {SCENES.map((_, j) => (
                    <span
                      key={j}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        j === active ? "w-6 bg-violet-500" : "w-1.5 bg-border"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: empilhado, altura normal, só entrada leve */}
      <div className="lg:hidden mt-10 space-y-16">
        {SCENES.map((s) => (
          <Reveal key={s.id}>
            <div className="space-y-5 text-center">
              <span className="inline-flex items-center rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-300">
                {s.badge}
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">{s.titulo}</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">{s.texto}</p>
              <div className="mx-auto w-full max-w-sm">
                <div className="shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)] rounded-2xl">
                  {s.render(surface)}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

/* ---- Mockups do produto (estilizados, superfícies sólidas do tema) ---- */

function MockFrame({ title, children, surface }) {
  return (
    <div className={`rounded-2xl border border-border ${surface} overflow-hidden`}>
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2.5">
        <Spark className="h-5 w-5" />
        <span className="text-xs font-semibold">{title}</span>
        <span className="ml-auto flex gap-1">
          <span className="h-2 w-2 rounded-full bg-border" />
          <span className="h-2 w-2 rounded-full bg-border" />
        </span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function MockHub({ surface }) {
  return (
    <MockFrame title="Hub" surface={surface}>
      <div className="space-y-3">
        <div className="rounded-xl border border-violet-500/40 bg-violet-500/10 p-3.5 space-y-1">
          <p className="text-[10px] uppercase tracking-wide text-violet-300 font-semibold">Agora</p>
          <p className="text-sm font-bold">Prova de Química · Estrutura atômica</p>
          <p className="text-xs text-muted-foreground">quinta-feira · faltam <strong className="text-violet-300">3 dias</strong></p>
          <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
            <div className="h-full w-2/3 rounded-full bg-violet-500" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="rounded-xl bg-secondary border border-border p-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold"><Flame className="h-3.5 w-3.5 text-amber-400" /> Ofensiva</p>
            <p className="text-lg font-bold">5 dias</p>
          </div>
          <div className="rounded-xl bg-secondary border border-border p-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold"><Radar className="h-3.5 w-3.5 text-violet-400" /> Foco</p>
            <p className="text-xs text-muted-foreground mt-1">Nomenclatura de cadeias</p>
          </div>
        </div>
        <div className="rounded-xl bg-secondary border border-border px-3 py-2.5 flex items-center gap-2 text-xs text-muted-foreground">
          <ListChecks className="h-3.5 w-3.5 text-violet-400 shrink-0" /> Revisar flashcards de História · 12 cards pra hoje
        </div>
      </div>
    </MockFrame>
  );
}

function MockRotina({ surface }) {
  const bloco = (cls, label) => (
    <div className={`rounded-md px-1.5 py-1 text-[8px] font-semibold leading-tight ${cls}`}>{label}</div>
  );
  return (
    <MockFrame title="Rotina" surface={surface}>
      <div className="grid grid-cols-5 gap-1.5 text-center">
        {["Seg", "Ter", "Qua", "Qui", "Sex"].map((d, i) => (
          <div key={d} className="space-y-1.5">
            <p className={`text-[9px] font-semibold ${i === 3 ? "text-violet-300" : "text-muted-foreground"}`}>{d}</p>
            <div className="space-y-1">
              {bloco("bg-secondary border border-border text-muted-foreground", "Escola 7h–12h45")}
              {i === 0 && bloco("bg-violet-500/20 border border-violet-500/40 text-violet-200", "Estudo · Química")}
              {i === 1 && bloco("bg-emerald-500/15 border border-emerald-500/30 text-emerald-300", "Jiu-jitsu 18h")}
              {i === 2 && bloco("bg-violet-500/20 border border-violet-500/40 text-violet-200", "Estudo · revisão")}
              {i === 3 && bloco("bg-violet-600 text-white", "PROVA · Química")}
              {i === 4 && bloco("bg-emerald-500/15 border border-emerald-500/30 text-emerald-300", "Jiu-jitsu 18h")}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <Calendar className="h-3 w-3 text-violet-400" /> Arrasta, solta e redimensiona — igual agenda de gente grande
      </p>
    </MockFrame>
  );
}

function MockChat({ surface }) {
  return (
    <MockFrame title="Chat" surface={surface}>
      <div className="space-y-2.5 text-xs">
        <div className="rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 flex items-center gap-2">
          <GraduationCap className="h-3.5 w-3.5 text-violet-300 shrink-0" />
          <span className="text-violet-200 font-medium">Modo Prova ativo: Trimestral de Química · quinta</span>
        </div>
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-2xl rounded-br-md bg-violet-600 text-white px-3 py-2">
            Cai isomeria nessa prova?
          </div>
        </div>
        <div className="flex justify-start">
          <div className="max-w-[90%] rounded-2xl rounded-bl-md bg-secondary px-3 py-2 space-y-1">
            <p>Não! Pelo roteiro que você me passou, isomeria fica pra P4. 👀</p>
            <p className="text-muted-foreground">O que cai é <strong className="text-violet-300">nomenclatura e funções orgânicas</strong> — e é aí que você mais errou. Bora revisar isso?</p>
          </div>
        </div>
        <div className="flex gap-1.5">
          <span className="rounded-full border border-border bg-secondary px-2.5 py-1 text-[10px] text-muted-foreground">⚡ Quiz disso</span>
          <span className="rounded-full border border-border bg-secondary px-2.5 py-1 text-[10px] text-muted-foreground">🃏 Virar flashcards</span>
        </div>
      </div>
    </MockFrame>
  );
}

// Celular: foto da folha no chat → IA lê e corrige
function MockPhone({ surface }) {
  return (
    <div className="mx-auto w-60 sm:w-64">
      {/* moldura de celular */}
      <div className={`rounded-[2.2rem] border-[6px] border-border ${surface} overflow-hidden`}>
        <div className="flex justify-center pt-2 pb-1">
          <span className="h-1.5 w-16 rounded-full bg-border" />
        </div>
        <div className="px-3 pb-4 space-y-2.5 text-xs text-left">
          <div className="flex items-center gap-1.5 border-b border-border/60 pb-2">
            <Spark className="h-4 w-4" />
            <span className="text-[10px] font-semibold">Seu tutor</span>
          </div>
          {/* foto enviada pelo aluno */}
          <div className="flex justify-end">
            <div className="w-[80%] rounded-2xl rounded-br-md bg-violet-600 p-1.5">
              <div className="rounded-xl bg-[#F5F2E8] p-2.5 space-y-1.5">
                <p className="text-[8px] font-bold text-zinc-700">Atividade — Funções orgânicas</p>
                {[..."abc"].map((l) => (
                  <div key={l} className="space-y-1">
                    <div className="h-1 w-full rounded bg-zinc-400/60" />
                    <div className="h-1 w-4/5 rounded bg-zinc-400/40" />
                  </div>
                ))}
                <p className="text-[8px] italic text-zinc-500">✏️ resposta à lápis…</p>
              </div>
              <p className="px-1.5 pt-1 text-[9px] text-white/90 flex items-center gap-1">
                <Camera className="h-2.5 w-2.5" /> foto enviada
              </p>
            </div>
          </div>
          {/* resposta da IA */}
          <div className="flex justify-start">
            <div className="max-w-[90%] rounded-2xl rounded-bl-md bg-secondary px-3 py-2 space-y-1 text-[10px]">
              <p>Li sua folha! ✅ Você acertou a <strong className="text-violet-300">1</strong> e a <strong className="text-violet-300">2</strong>.</p>
              <p className="text-muted-foreground">Na 3 você confundiu éter com éster — o éster tem C=O ligado ao O. Quer que eu explique com um exemplo?</p>
            </div>
          </div>
          <div className="flex justify-start">
            <span className="rounded-full border border-violet-500/40 bg-violet-500/10 px-2.5 py-1 text-[9px] text-violet-300">
              ⚡ Refazer a questão 3 comigo
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ComoFunciona({ surface }) {
  return (
    <div className="space-y-10">
      <Reveal className="text-center space-y-2">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Como funciona</h2>
        <p className="text-sm text-muted-foreground">O loop que faz seu tutor ficar cada vez melhor.</p>
      </Reveal>
      <div className="grid gap-4 sm:grid-cols-3 max-w-4xl mx-auto">
        <Reveal><Step surface={surface} n="1" titulo="Conte como você aprende" texto="3 perguntas rápidas no primeiro acesso: seu jeito de aprender, o que te trava, seus objetivos." /></Reveal>
        <Reveal delay={100}><Step surface={surface} n="2" titulo="Estude do seu jeito" texto="Chat, quiz e flashcards — tudo adaptado ao seu perfil e focado no que cai na sua prova." /></Reveal>
        <Reveal delay={200}><Step surface={surface} n="3" titulo="Ele aprende com você" texto="Cada quiz alimenta o radar de pontos fracos. Seu tutor sabe onde apertar — e melhora a cada uso." /></Reveal>
      </div>
    </div>
  );
}

// O que não virou cena no showcase vive aqui: grid compacto com entrada em stagger
const GRID_FEATURES = [
  { icon: GraduationCap, titulo: "Modo Prova", texto: "Trava a IA 100% no conteúdo que vai cair na sua prova." },
  { icon: Layers, titulo: "Flashcards", texto: "Cards do seu material, com revisão espaçada automática." },
  { icon: ListChecks, titulo: "Quiz / Simulado", texto: "Questões no estilo da prova, com explicação que ensina." },
  { icon: Brain, titulo: "Mapa mental", texto: "A matéria inteira organizada num mapa interativo." },
  { icon: Radar, titulo: "Radar de pontos fracos", texto: "Descobre onde você mais erra e foca a revisão ali." },
  { icon: UserRound, titulo: "Perfil do aluno", texto: "Sua ficha de estudo: o motor que personaliza tudo." },
];

const gridStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
};
const gridCard = {
  hidden: { opacity: 0, y: 26, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

function FeaturesGrid({ surface }) {
  const reduce = useReducedMotion();
  const anim = reduce
    ? {}
    : { variants: gridStagger, initial: "hidden", whileInView: "show", viewport: { once: true, amount: 0.15 } };
  return (
    <div className="space-y-10">
      <Reveal className="text-center space-y-2">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">E ainda tem mais</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">Não é só um chat. É um sistema de estudo completo — que gira em torno de você.</p>
      </Reveal>
      <motion.div {...anim} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {GRID_FEATURES.map((f) => (
          <motion.div key={f.titulo} variants={reduce ? undefined : gridCard}>
            <Feature surface={surface} icon={f.icon} titulo={f.titulo} texto={f.texto} />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

function Privacidade({ surface }) {
  return (
    <Reveal>
      <div className={`rounded-3xl border border-border ${surface} px-6 py-12 sm:px-12 space-y-8`}>
        <div className="text-center space-y-3 max-w-2xl mx-auto">
          <div className="inline-flex rounded-2xl bg-emerald-500/10 p-3">
            <Lock className="h-6 w-6 text-emerald-400" strokeWidth={1.75} />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Seus dados são só seus</h2>
          <p className="text-muted-foreground">
            Pro tutor te conhecer, você conta como estuda. Isso é coisa sua — e a gente trata assim.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 max-w-4xl mx-auto">
          <PrivacyItem icon={KeyRound} titulo="Protegido por login" texto="Seu perfil só é acessível pela sua conta. Ninguém mais vê." />
          <PrivacyItem icon={EyeOff} titulo="Nunca vendido" texto="Não vendemos nem compartilhamos seus dados com terceiros. Ponto." />
          <PrivacyItem icon={ShieldCheck} titulo="Senha criptografada" texto="Sua senha é guardada criptografada e trafega em conexão segura." />
          <PrivacyItem icon={Trash2} titulo="Apague quando quiser" texto="Pediu pra excluir a conta? Seus dados vão junto." />
        </div>
        <p className="text-center text-xs text-muted-foreground">
          Tudo conforme a <strong className="text-foreground/80">LGPD</strong>. Detalhes em linguagem simples na nossa{" "}
          <Link to="/privacidade" className="text-violet-400 hover:text-violet-300 underline underline-offset-2">Política de Privacidade</Link>.
        </p>
      </div>
    </Reveal>
  );
}

// Depoimentos aprovados pelo admin (só aparece quando existir pelo menos 1)
function Depoimentos({ surface, items }) {
  return (
    <div className="space-y-10" data-testid="landing-depoimentos">
      <Reveal className="text-center space-y-2">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Quem usa, recomenda</h2>
        <p className="text-sm text-muted-foreground">Avaliações reais de alunos, publicadas com autorização.</p>
      </Reveal>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto">
        {items.slice(0, 6).map((d, i) => (
          <Reveal key={i} delay={i * 80}>
            <div className={`h-full rounded-2xl border border-border ${surface} p-5 space-y-3`}>
              <Quote className="h-4 w-4 text-violet-400" />
              <p className="text-sm text-foreground/90">{d.comment}</p>
              <div className="flex items-center justify-between">
                <span className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className={`h-3.5 w-3.5 ${s <= d.stars ? "text-amber-400 fill-amber-400" : "text-border"}`} />
                  ))}
                </span>
                <span className="text-[11px] text-muted-foreground">Aluno(a)</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

function Planos({ surface, p }) {
  return (
    <div className="space-y-10">
      <Reveal className="text-center space-y-2">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Planos</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          O Grátis já te ajuda de verdade. O Médio tira os limites. O Pro libera tudo.
        </p>
      </Reveal>
      <div className="grid gap-4 md:grid-cols-3 max-w-4xl mx-auto">
        {ORDER.map((plano, i) => (
          <Reveal key={plano} delay={i * 100}>
            <PlanCard
              surface={surface}
              plano={plano}
              nome={p.nomes?.[plano] || plano}
              preco={p.precos?.[plano] ?? 0}
              tabela={p.tabela || []}
              popular={plano === "medio"}
            />
          </Reveal>
        ))}
      </div>
    </div>
  );
}

function FaqSection({ surface }) {
  return (
    <div className="space-y-10">
      <Reveal className="text-center space-y-2">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Perguntas frequentes</h2>
      </Reveal>
      <div className="max-w-2xl mx-auto space-y-3">
        <Faq surface={surface} q="Qual a diferença pra um ChatGPT da vida?" a="Um chat genérico responde qualquer coisa pra qualquer pessoa — e não lembra de você. Aqui, o tutor conhece SEU perfil: como você aprende, o que te trava, suas matérias fracas e o que cai na sua prova. Cada resposta é adaptada a isso, e ele melhora a cada uso." />
        <Faq surface={surface} q="Preciso pagar pra começar?" a="Não. O plano Grátis é de verdade: chat com IA personalizado, quiz e flashcards todos os dias, sem cartão de crédito. Você só paga se quiser tirar os limites." />
        <Faq surface={surface} q="Meus dados e meu perfil ficam protegidos?" a="Sim. Seu perfil de estudo é privado: só a sua conta acessa, a senha é criptografada e a gente NUNCA vende nem compartilha seus dados — tudo seguindo a LGPD (Lei Geral de Proteção de Dados). Eles servem pra uma coisa só: personalizar o SEU tutor. Detalhes na Política de Privacidade." />
        <Faq surface={surface} q="Funciona no celular?" a="Funciona — e bem. O app foi feito pensando no celular: dá pra conversar com o tutor, tirar foto do caderno e mandar no chat, revisar flashcards e ver sua rotina de qualquer lugar, direto do navegador, sem instalar nada." />
        <Faq surface={surface} q="Serve pra qual série ou escola? Que matérias cobre?" a="Pra qualquer aluno do Brasil: Fundamental II, Ensino Médio, ENEM e vestibulares — de Matemática e Química a História e Redação. A IA se adapta ao SEU material e ao SEU perfil: você diz o que vai cair e como aprende melhor, e ela foca nisso." />
        <Faq surface={surface} q="Como cancelo se não gostar?" a="Direto no app, em Perfil → Cancelar assinatura — sem e-mail, sem burocracia. Você continua com o plano até o fim do período já pago e depois volta pro Grátis automaticamente, sem multa e sem perder seu histórico nem seu perfil." />
      </div>
    </div>
  );
}

function CtaFinal() {
  return (
    <Reveal>
      <div className="rounded-3xl border border-violet-500/30 bg-violet-500/5 px-6 py-14 text-center space-y-5">
        <Spark className="h-12 w-12 mx-auto" />
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-xl mx-auto">
          Quanto antes ele te conhecer, melhor ele fica
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Crie sua conta, responda 3 perguntas rápidas e estude com um tutor feito pra você.
        </p>
        <Link
          to="/planos"
          data-testid="landing-final-cta"
          className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-medium px-7 py-3.5 transition duration-200 glow-violet"
        >
          Quero entrar <ArrowRight className="h-4 w-4" />
        </Link>
        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Leva menos de 1 minuto · Sem cartão
        </p>
      </div>

      {/* Bloco de confiança (4.5): privacidade + LGPD, pertinho do rodapé */}
      <div data-testid="landing-confianca" className="mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-xs text-muted-foreground">
        <span>🔒 Seus dados são privados e criptografados · LGPD</span>
        <span aria-hidden="true">—</span>
        <span>
          <Link to="/termos" className="text-violet-400 hover:text-violet-300 underline underline-offset-2">Termos de Uso</Link>
          {" · "}
          <Link to="/privacidade" className="text-violet-400 hover:text-violet-300 underline underline-offset-2">Política de Privacidade</Link>
        </span>
      </div>
    </Reveal>
  );
}

/* ================= Peças menores ================= */

function CompareRow({ texto, bad = false }) {
  return (
    <li className="flex items-start gap-2.5">
      {bad
        ? <X className="h-4 w-4 text-muted-foreground/60 mt-0.5 shrink-0" />
        : <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />}
      <span className={bad ? "" : "text-foreground/90"}>{texto}</span>
    </li>
  );
}

function Step({ n, titulo, texto, surface }) {
  return (
    <div className={`h-full rounded-2xl border border-border ${surface} p-5 space-y-2.5`}>
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-violet-600 text-white text-sm font-bold">{n}</span>
      <h3 className="font-bold tracking-tight">{titulo}</h3>
      <p className="text-sm text-muted-foreground">{texto}</p>
    </div>
  );
}

function Feature({ icon: Icon, titulo, texto, surface }) {
  return (
    <div className={`h-full rounded-2xl border border-border ${surface} hover-lift hover:border-violet-500/40 p-5 space-y-2.5 transition duration-200`}>
      <div className="inline-flex rounded-xl bg-violet-500/10 p-2.5">
        <Icon className="h-5 w-5 text-violet-400" strokeWidth={1.75} />
      </div>
      <h3 className="font-bold tracking-tight">{titulo}</h3>
      <p className="text-sm text-muted-foreground">{texto}</p>
    </div>
  );
}

function PrivacyItem({ icon: Icon, titulo, texto }) {
  return (
    <div className="space-y-2 text-center sm:text-left">
      <div className="inline-flex rounded-xl bg-emerald-500/10 p-2.5">
        <Icon className="h-5 w-5 text-emerald-400" strokeWidth={1.75} />
      </div>
      <h3 className="font-semibold text-sm tracking-tight">{titulo}</h3>
      <p className="text-xs text-muted-foreground leading-relaxed">{texto}</p>
    </div>
  );
}

function PlanCard({ plano, nome, preco, tabela, popular, surface }) {
  const destaque = popular ? "border-violet-500/60 shadow-lg shadow-violet-500/10" : "border-border";
  return (
    <div data-testid={`landing-plan-${plano}`} className={`relative h-full rounded-2xl border ${surface} hover-lift p-5 flex flex-col gap-4 ${destaque}`}>
      {popular && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-violet-600 text-white text-[10px] font-semibold uppercase tracking-wide px-3 py-0.5">
          Mais popular
        </span>
      )}
      <div className="text-center space-y-1 pt-1">
        <p className="flex items-center justify-center gap-1.5 font-bold tracking-tight">
          {plano === "pro" ? <Crown className="h-4 w-4 text-amber-400" /> : plano === "medio" ? <Sparkles className="h-4 w-4 text-violet-400" /> : null}
          {nome}
        </p>
        <p className="text-2xl font-bold">
          {preco > 0 ? (
            <>R$ {preco.toFixed(2).replace(".", ",")}<span className="text-xs font-normal text-muted-foreground">/mês</span></>
          ) : (
            <>R$ 0</>
          )}
        </p>
      </div>
      <ul className="space-y-2 text-xs flex-1">
        {tabela.map((row) => (
          <li key={row.recurso} className="flex items-start gap-2">
            {row[plano] === false
              ? <X className="h-3.5 w-3.5 text-muted-foreground/50 mt-0.5 shrink-0" />
              : <Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />}
            <span className="text-muted-foreground">
              <span className="text-foreground/90">{row.recurso}</span>
              {typeof row[plano] === "string" && <span className="block text-[11px]">{row[plano]}</span>}
            </span>
          </li>
        ))}
      </ul>
      <Link
        to={`/cadastro?plano=${plano}`}
        data-testid={`landing-plan-cta-${plano}`}
        className={`rounded-full text-center py-2.5 text-sm font-medium transition duration-200 ${
          popular
            ? "bg-violet-600 hover:bg-violet-500 text-white"
            : "border border-border hover:border-violet-500/50 text-foreground/90"
        }`}
      >
        {plano === "gratis" ? "Começar grátis" : `Começar com o ${nome}`}
      </Link>
    </div>
  );
}

function Faq({ q, a, surface }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border border-border ${surface}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left text-sm font-semibold"
      >
        {q}
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-4 text-sm text-muted-foreground">{a}</p>}
    </div>
  );
}
